import { DeliverableIdSchema } from '@dpem/ontology';
import * as z from 'zod';

import { PageQuerySchema, paginated } from '../common/pagination.js';
import {
  DescriptionSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  MarkdownSchema,
  QueryIntSchema,
  ShortTextSchema,
  TagListSchema,
  TextFilterSchema,
  sortBy,
} from '../common/primitives.js';
import { AttachmentSchema, ExternalLinkSchema } from './attachments.js';

/**
 * deliverables context — work products and their versioned template bodies.
 * "Template" alone is banned vocabulary (architecture §3): the work product is a
 * Deliverable, its markdown body is a TemplateVersion.
 */

export const DELIVERABLE_SORT_FIELDS = ['name', 'updatedAt', 'createdAt', 'usageCount'] as const;

/** Monotonically increasing per deliverable, immutable once published (architecture §3). */
export const TemplateVersionNumberSchema = z.int().min(1).max(100_000);

export const TemplateVersionSchema = z.strictObject({
  deliverableId: DeliverableIdSchema,
  versionNumber: TemplateVersionNumberSchema,
  body: MarkdownSchema,
  changeNote: DescriptionSchema.nullable(),
  /** True once a published method references it — the body can then never change. */
  frozen: z.boolean(),
  createdAt: IsoDateTimeSchema,
  createdBy: ShortTextSchema,
});
export type TemplateVersion = z.infer<typeof TemplateVersionSchema>;

export const DeliverableSchema = z.strictObject({
  id: DeliverableIdSchema,
  /**
   * The deliverable's row uuid, which is what `attachments` and `external-links` mean
   * by `ownerId`. `id` above is the ontology id (`deliv:HLD`) and stays the identity
   * every binding and URL uses; see the note on `recordId` in `catalog.ts` for why the
   * two coexist.
   */
  recordId: IdentifierSchema,
  name: LabelSchema,
  description: DescriptionSchema.nullable(),
  category: LabelSchema.nullable(),
  tags: TagListSchema,
  /** Legacy `templateFile` value, preserved so exports round-trip byte-identically. */
  templateFile: ShortTextSchema.nullable(),
  currentVersionNumber: TemplateVersionNumberSchema.nullable(),
  versionCount: z.int().min(0),
  usageCount: z.int().min(0),
  attachments: z.array(AttachmentSchema),
  links: z.array(ExternalLinkSchema),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  createdBy: ShortTextSchema,
});
export type Deliverable = z.infer<typeof DeliverableSchema>;

export const ListDeliverablesRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...sortBy(DELIVERABLE_SORT_FIELDS).shape,
  category: LabelSchema.optional(),
  minUsageCount: QueryIntSchema.optional(),
});
export type ListDeliverablesRequest = z.infer<typeof ListDeliverablesRequestSchema>;
export const ListDeliverablesResponseSchema = paginated(DeliverableSchema);
export type ListDeliverablesResponse = z.infer<typeof ListDeliverablesResponseSchema>;

export const CreateDeliverableRequestSchema = z.strictObject({
  id: DeliverableIdSchema.optional(),
  name: LabelSchema,
  description: DescriptionSchema.optional(),
  category: LabelSchema.optional(),
  tags: TagListSchema.optional(),
  templateFile: ShortTextSchema.optional(),
  /** Seeds version 1 in the same transaction; a deliverable with no body is useless. */
  body: MarkdownSchema.optional(),
});
export type CreateDeliverableRequest = z.infer<typeof CreateDeliverableRequestSchema>;
export const CreateDeliverableResponseSchema = z.strictObject({
  deliverable: DeliverableSchema,
  version: TemplateVersionSchema.nullable(),
});
export type CreateDeliverableResponse = z.infer<typeof CreateDeliverableResponseSchema>;

export const GetDeliverableRequestSchema = z.strictObject({
  deliverableId: DeliverableIdSchema,
  /** Include the current template body inline — the editor's first paint needs it. */
  includeCurrentBody: z.stringbool().optional(),
});
export type GetDeliverableRequest = z.infer<typeof GetDeliverableRequestSchema>;
export const GetDeliverableResponseSchema = z.strictObject({
  deliverable: DeliverableSchema,
  currentVersion: TemplateVersionSchema.nullable(),
});
export type GetDeliverableResponse = z.infer<typeof GetDeliverableResponseSchema>;

export const UpdateDeliverableRequestSchema = z.strictObject({
  deliverableId: DeliverableIdSchema,
  name: LabelSchema,
  description: DescriptionSchema.optional(),
  category: LabelSchema.optional(),
  tags: TagListSchema.optional(),
  templateFile: ShortTextSchema.optional(),
});
export type UpdateDeliverableRequest = z.infer<typeof UpdateDeliverableRequestSchema>;
export const UpdateDeliverableResponseSchema = z.strictObject({ deliverable: DeliverableSchema });
export type UpdateDeliverableResponse = z.infer<typeof UpdateDeliverableResponseSchema>;

export const DeleteDeliverableRequestSchema = z.strictObject({
  deliverableId: DeliverableIdSchema,
  force: z.stringbool().optional(),
});
export type DeleteDeliverableRequest = z.infer<typeof DeleteDeliverableRequestSchema>;
export const DeleteDeliverableResponseSchema = z.strictObject({
  deliverableId: DeliverableIdSchema,
  deletedAt: IsoDateTimeSchema,
  unboundActivityCount: z.int().min(0),
});
export type DeleteDeliverableResponse = z.infer<typeof DeleteDeliverableResponseSchema>;

export const CreateTemplateVersionRequestSchema = z.strictObject({
  deliverableId: DeliverableIdSchema,
  body: MarkdownSchema,
  changeNote: DescriptionSchema.optional(),
  /** Guards against two editors branching the body from the same base version. */
  expectedCurrentVersionNumber: TemplateVersionNumberSchema.optional(),
});
export type CreateTemplateVersionRequest = z.infer<typeof CreateTemplateVersionRequestSchema>;
export const CreateTemplateVersionResponseSchema = z.strictObject({
  version: TemplateVersionSchema,
});
export type CreateTemplateVersionResponse = z.infer<typeof CreateTemplateVersionResponseSchema>;

export const GetTemplateVersionRequestSchema = z.strictObject({
  deliverableId: DeliverableIdSchema,
  versionNumber: QueryIntSchema,
});
export type GetTemplateVersionRequest = z.infer<typeof GetTemplateVersionRequestSchema>;
export const GetTemplateVersionResponseSchema = z.strictObject({
  version: TemplateVersionSchema,
});
export type GetTemplateVersionResponse = z.infer<typeof GetTemplateVersionResponseSchema>;

export const ListTemplateVersionsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  deliverableId: DeliverableIdSchema,
  /** Bodies are large; the list omits them unless asked. */
  includeBodies: z.stringbool().optional(),
});
export type ListTemplateVersionsRequest = z.infer<typeof ListTemplateVersionsRequestSchema>;
export const TemplateVersionSummarySchema = TemplateVersionSchema.omit({ body: true }).extend({
  body: MarkdownSchema.nullable(),
  bodyLength: z.int().min(0),
});
export type TemplateVersionSummary = z.infer<typeof TemplateVersionSummarySchema>;
export const ListTemplateVersionsResponseSchema = paginated(TemplateVersionSummarySchema);
export type ListTemplateVersionsResponse = z.infer<typeof ListTemplateVersionsResponseSchema>;
